import { useState } from 'react';
import useSWR from 'swr';
import { toast } from 'App';
import {
  CreatePermissionGate,
  DeletePermissionGate,
  UpdatePermissionGate,
} from 'components/PermissionGate';
import { PERMISSION_RESOURCES } from 'constants/permissions';
import { adminService } from 'views/services/adminService';
import { invoicePaymentsService } from 'views/services/invoicePaymentsService';
import { EConfigStatus, IInvoiceRouteRule } from 'views/types/invoicePayments';
import {
  cellClass,
  dangerSmallBtn,
  emptyToNull,
  headerClass,
  inputCls,
  labelCls,
  primaryBtn,
  secondaryBtn,
  smallBtn,
} from './shared';

interface IDraft {
  currency: string;
  beneficiary_country: string;
  beneficiary_bank_country: string;
  bank_country_group: string;
  sender_id: string;
  rail: string;
  priority: string;
  min_amount: string;
  max_amount: string;
  required_fields: string;
  status: EConfigStatus;
}

const EMPTY: IDraft = {
  currency: 'USD',
  beneficiary_country: '',
  beneficiary_bank_country: '',
  bank_country_group: '',
  sender_id: '',
  rail: 'SWIFT',
  priority: '100',
  min_amount: '',
  max_amount: '',
  required_fields: '',
  status: EConfigStatus.ACTIVE,
};

export const RouteRulesPanel = () => {
  const [draft, setDraft] = useState<IDraft>(EMPTY);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: rules, mutate } = useSWR(
    '/api/admin/invoice-payment-config/route-rules',
    () => invoicePaymentsService.getRouteRules()
  );
  const { data: meta } = useSWR('/api/admin/invoice-payment-config/meta', () =>
    invoicePaymentsService.getMeta()
  );
  const { data: senders } = useSWR(
    '/api/admin/invoice-payment-config/senders',
    () => invoicePaymentsService.getSenders()
  );

  const senderName = (id?: string | null) =>
    id ? (senders?.find(s => s.id === id)?.name ?? id.slice(0, 8)) : 'Any';

  const startEdit = (rule: IInvoiceRouteRule) => {
    setEditingId(rule.id);
    setDraft({
      currency: rule.currency,
      beneficiary_country: rule.beneficiary_country ?? '',
      beneficiary_bank_country: rule.beneficiary_bank_country ?? '',
      bank_country_group: rule.bank_country_group ?? '',
      sender_id: rule.sender_id ?? '',
      rail: rule.rail,
      priority: String(rule.priority),
      min_amount: rule.min_amount ?? '',
      max_amount: rule.max_amount ?? '',
      required_fields: (rule.required_fields ?? []).join(', '),
      status: rule.status,
    });
  };

  const submit = async () => {
    const payload = {
      currency: draft.currency.trim().toUpperCase(),
      beneficiary_country: emptyToNull(draft.beneficiary_country.toUpperCase()),
      beneficiary_bank_country: emptyToNull(
        draft.beneficiary_bank_country.toUpperCase()
      ),
      bank_country_group: emptyToNull(draft.bank_country_group),
      sender_id: emptyToNull(draft.sender_id),
      rail: draft.rail,
      priority: Number(draft.priority) || 100,
      min_amount: emptyToNull(draft.min_amount),
      max_amount: emptyToNull(draft.max_amount),
      required_fields: draft.required_fields
        .split(',')
        .map(field => field.trim())
        .filter(Boolean),
      status: draft.status,
    };
    try {
      setBusy(true);
      if (editingId) {
        await invoicePaymentsService.updateRouteRule(editingId, payload);
        toast({ title: 'Route rule updated', status: 'success' });
      } else {
        await invoicePaymentsService.createRouteRule(payload);
        toast({ title: 'Route rule created', status: 'success' });
      }
      setDraft(EMPTY);
      setEditingId(null);
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this route rule?')) return;
    try {
      setBusy(true);
      await invoicePaymentsService.deleteRouteRule(id);
      toast({ title: 'Route rule deleted', status: 'success' });
      await mutate();
    } catch (error) {
      adminService.errorHandler(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <CreatePermissionGate resource={PERMISSION_RESOURCES.INVOICE_CONFIG}>
        <div className="mb-4 grid grid-cols-2 gap-3 rounded border border-gray-200 p-3 dark:border-white/20 md:grid-cols-4 xl:grid-cols-6">
          <div>
            <label className={labelCls}>Currency</label>
            <input
              className={inputCls}
              value={draft.currency}
              onChange={e => setDraft({ ...draft, currency: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>
              Beneficiary country (empty = any)
            </label>
            <input
              className={inputCls}
              maxLength={2}
              placeholder="ISO-2"
              value={draft.beneficiary_country}
              onChange={e =>
                setDraft({ ...draft, beneficiary_country: e.target.value })
              }
            />
          </div>
          <div>
            <label className={labelCls}>Bank country (empty = any)</label>
            <input
              className={inputCls}
              maxLength={2}
              placeholder="ISO-2"
              value={draft.beneficiary_bank_country}
              onChange={e =>
                setDraft({
                  ...draft,
                  beneficiary_bank_country: e.target.value,
                })
              }
            />
          </div>
          <div>
            <label className={labelCls}>Bank country group</label>
            <select
              className={inputCls}
              value={draft.bank_country_group}
              onChange={e =>
                setDraft({ ...draft, bank_country_group: e.target.value })
              }
            >
              <option value="">Any</option>
              {meta?.bankCountryGroups.map(group => (
                <option key={group} value={group}>
                  {group}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Sender</label>
            <select
              className={inputCls}
              value={draft.sender_id}
              onChange={e => setDraft({ ...draft, sender_id: e.target.value })}
            >
              <option value="">Any</option>
              {senders?.map(sender => (
                <option key={sender.id} value={sender.id}>
                  {sender.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Payment method (result)</label>
            <select
              className={inputCls}
              value={draft.rail}
              onChange={e => setDraft({ ...draft, rail: e.target.value })}
            >
              {meta?.rails.map(rail => (
                <option key={rail} value={rail}>
                  {rail}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Priority (lower wins)</label>
            <input
              className={inputCls}
              value={draft.priority}
              onChange={e => setDraft({ ...draft, priority: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>Min amount</label>
            <input
              className={inputCls}
              value={draft.min_amount}
              onChange={e => setDraft({ ...draft, min_amount: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>Max amount</label>
            <input
              className={inputCls}
              value={draft.max_amount}
              onChange={e => setDraft({ ...draft, max_amount: e.target.value })}
            />
          </div>
          <div className="col-span-2">
            <label className={labelCls}>
              Required bank fields (comma-separated)
            </label>
            <input
              className={inputCls}
              placeholder="iban, bic_swift"
              value={draft.required_fields}
              onChange={e =>
                setDraft({ ...draft, required_fields: e.target.value })
              }
            />
          </div>
          <div className="flex items-end gap-2">
            <button disabled={busy} onClick={submit} className={primaryBtn}>
              {editingId ? 'Save' : 'Add rule'}
            </button>
            {editingId && (
              <button
                className={secondaryBtn}
                onClick={() => {
                  setEditingId(null);
                  setDraft(EMPTY);
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </CreatePermissionGate>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr>
              <th className={headerClass}>Priority</th>
              <th className={headerClass}>Currency</th>
              <th className={headerClass}>Beneficiary</th>
              <th className={headerClass}>Bank country</th>
              <th className={headerClass}>Group</th>
              <th className={headerClass}>Sender</th>
              <th className={headerClass}>Method</th>
              <th className={headerClass}>Amount range</th>
              <th className={headerClass}>Required fields</th>
              <th className={headerClass}>Status</th>
              <th className={headerClass}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rules?.map(rule => (
              <tr key={rule.id}>
                <td className={cellClass}>{rule.priority}</td>
                <td className={cellClass}>{rule.currency}</td>
                <td className={cellClass}>
                  {rule.beneficiary_country ?? 'Any'}
                </td>
                <td className={cellClass}>
                  {rule.beneficiary_bank_country ?? 'Any'}
                </td>
                <td className={cellClass}>{rule.bank_country_group ?? '—'}</td>
                <td className={cellClass}>{senderName(rule.sender_id)}</td>
                <td className={cellClass}>{rule.rail}</td>
                <td className={cellClass}>
                  {rule.min_amount ?? '0'} – {rule.max_amount ?? '∞'}
                </td>
                <td className={`${cellClass} max-w-[220px] truncate`}>
                  {(rule.required_fields ?? []).join(', ') || '—'}
                </td>
                <td className={cellClass}>
                  <span
                    className={
                      rule.status === EConfigStatus.ACTIVE
                        ? 'text-green-600'
                        : 'text-gray-400'
                    }
                  >
                    {rule.status}
                  </span>
                </td>
                <td className={`${cellClass} flex gap-2`}>
                  <UpdatePermissionGate
                    resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
                  >
                    <button
                      className={smallBtn}
                      onClick={() => startEdit(rule)}
                    >
                      Edit
                    </button>
                  </UpdatePermissionGate>
                  <DeletePermissionGate
                    resource={PERMISSION_RESOURCES.INVOICE_CONFIG}
                  >
                    <button
                      className={dangerSmallBtn}
                      disabled={busy}
                      onClick={() => remove(rule.id)}
                    >
                      Delete
                    </button>
                  </DeletePermissionGate>
                </td>
              </tr>
            ))}
            {rules && rules.length === 0 && (
              <tr>
                <td
                  className="py-4 text-center text-sm text-gray-500"
                  colSpan={11}
                >
                  No route rules.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
